// frontend/src/pages/Diario/DiarioCuidadosPage.js (versão 3.0.0)
// REFATORAÇÃO: Layout focado em Checklist Prioritário
// Removido PageTitle e info box - mantém apenas componentes essenciais

import React, { useEffect, useState, useCallback, useMemo } from "react";

import { usePonto } from "../../hooks/usePonto";
import { usePacientes } from "../../hooks/usePacientes";
import { useAgendaTarefas } from "../../hooks/useAgendaTarefas";
import { useDiarioCuidados } from "../../hooks/useDiarioCuidados";
import api from "../../api/api";

// Componentes Comuns
import LoadingIndicator from "../../components/Common/LoadingIndicator";
import Alert from "../../components/UI/Alert";
import ConfirmacaoModal from "../../components/Common/ConfirmacaoModal";
import SinaisVitaisModal from "../../components/Diario/SinaisVitaisModal";

// Modais de Registro
import ModalAlimentacao from "../../components/Diario/ModalAlimentacao";
import ModalHigiene from "../../components/Diario/ModalHigiene";
import ModalPosicionamento from "../../components/Diario/ModalPosicionamento";
import ModalObservacaoLivre from "../../components/Diario/ModalObservacaoLivre";
import ModalAtividadeFisica from "../../components/Diario/ModalAtividadeFisica";
import ModalPesagem from "../../components/Diario/ModalPesagem";
import ModalGlicemia from "../../components/Diario/ModalGlicemia";

// Componentes do Diário - Novo Layout
import ChecklistPrioritario from "../../components/Diario/ChecklistPrioritario";
import TimelineHistorico from "../../components/Diario/TimelineHistorico";
import BotaoRegistroFlutuante from "../../components/Diario/BotaoRegistroFlutuante";
import IndicadorProgressoTarefas from "../../components/Diario/IndicadorProgressoTarefas";

const DiarioCuidadosPage = () => {
    const { pontoStatus, isPontoLoading, pontoError, fetchPontoStatus } = usePonto();
    const pacienteIdEmAtendimento = pontoStatus?.data?.paciente_id;

    const { fetchPacienteById } = usePacientes();
    const [paciente, setPaciente] = useState(null);
    const [isLoadingPaciente, setIsLoadingPaciente] = useState(false);
    const [pacienteError, setPacienteError] = useState(null);

    const loadPaciente = useCallback(async (id) => {
        setIsLoadingPaciente(true);
        setPacienteError(null);
        try {
            const data = await fetchPacienteById(id);
            setPaciente(data);
        } catch (err) {
            setPacienteError(err.message || "Falha ao carregar paciente.");
        } finally {
            setIsLoadingPaciente(false);
        }
    }, [fetchPacienteById]);

    const {
        tarefas,
        isLoadingTarefas,
        errorTarefas,
        fetchTarefasPendentes,
        fetchTarefasConcluidas,
        removeTarefaDaLista,
        concluirTarefa,
    } = useAgendaTarefas(pacienteIdEmAtendimento);

    // Estado para armazenar tarefas concluídas do dia
    const [tarefasConcluidas, setTarefasConcluidas] = useState([]);
    const [isLoadingConcluidas, setIsLoadingConcluidas] = useState(false);

    // Função para buscar tarefas concluídas do dia atual (busca direta via API para não sobrescrever tarefas pendentes)
    const buscarTarefasConcluidasDoDia = useCallback(async () => {
        if (!pacienteIdEmAtendimento) return;
        
        setIsLoadingConcluidas(true);
        try {
            const hoje = new Date();
            const dateStart = hoje.toISOString().split('T')[0];
            const dateEnd = hoje.toISOString().split('T')[0];
            
            // Busca direta via API para não interferir com o estado de tarefas pendentes
            const url = `/agenda-tarefas/paciente/${pacienteIdEmAtendimento}/concluidas?dateStart=${dateStart}&dateEnd=${dateEnd}`;
            const response = await api.get(url);
            const concluidas = response.data.data || [];
            setTarefasConcluidas(concluidas);
        } catch (err) {
            console.error('Erro ao buscar tarefas concluídas:', err);
            setTarefasConcluidas([]);
        } finally {
            setIsLoadingConcluidas(false);
        }
    }, [pacienteIdEmAtendimento]);

    // Combina tarefas pendentes e concluídas para o cálculo de progresso
    const todasTarefasDoDia = useMemo(() => {
        return [...tarefas, ...tarefasConcluidas];
    }, [tarefas, tarefasConcluidas]);

    const {
        registros,
        isLoading: isLoadingRegistros,
        error: errorRegistros,
        fetchRegistros,
        createRegistro,
    } = useDiarioCuidados(pacienteIdEmAtendimento);

    // Estados de modal
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isSinaisVitaisModalOpen, setIsSinaisVitaisModalOpen] = useState(false);
    const [isAlimentacaoModalOpen, setIsAlimentacaoModalOpen] = useState(false);
    const [isHigieneModalOpen, setIsHigieneModalOpen] = useState(false);
    const [isPosicionamentoModalOpen, setIsPosicionamentoModalOpen] = useState(false);
    const [isObservacaoLivreModalOpen, setIsObservacaoLivreModalOpen] = useState(false);
    const [isAtividadeFisicaModalOpen, setIsAtividadeFisicaModalOpen] = useState(false);
    const [isPesagemModalOpen, setIsPesagemModalOpen] = useState(false);
    const [isGlicemiaModalOpen, setIsGlicemiaModalOpen] = useState(false);
    const [tarefaSelecionada, setTarefaSelecionada] = useState(null);

    const handleRegistroManualSucesso = () => {
        fetchRegistros();
    };

    // Mapeia tipo de tarefa para o modal correspondente
    const getModalParaTipoTarefa = (tipoTarefa) => {
        const tipoLower = tipoTarefa?.toLowerCase() || '';
        
        if (tipoLower.includes('sinais vitais')) return 'sinaisVitais';
        if (tipoLower.includes('alimentação') || tipoLower.includes('alimentacao')) return 'alimentacao';
        if (tipoLower.includes('higienização') || tipoLower.includes('higienizacao') || tipoLower.includes('higiene')) return 'higiene';
        if (tipoLower.includes('mudança de decúbito') || tipoLower.includes('mudanca de decubito') || tipoLower.includes('posicionamento')) return 'posicionamento';
        if (tipoLower.includes('atividade física') || tipoLower.includes('atividade fisica')) return 'atividadeFisica';
        if (tipoLower.includes('pesagem')) return 'pesagem';
        if (tipoLower.includes('glicemia')) return 'glicemia';
        if (tipoLower.includes('outro') || tipoLower.includes('observação') || tipoLower.includes('observacao')) return 'observacaoLivre';
        
        // Para medicação e outros tipos, usa confirmação padrão
        return 'confirmacao';
    };

    // Handler para quando o usuário clica em "Concluir" em uma tarefa
    const handleConcluirTarefa = (tarefa) => {
        setTarefaSelecionada(tarefa);
        
        const modalType = getModalParaTipoTarefa(tarefa.tipo_tarefa);
        
        // Fecha todos os modais primeiro
        setIsConfirmModalOpen(false);
        setIsSinaisVitaisModalOpen(false);
        setIsAlimentacaoModalOpen(false);
        setIsHigieneModalOpen(false);
        setIsPosicionamentoModalOpen(false);
        setIsObservacaoLivreModalOpen(false);
        setIsAtividadeFisicaModalOpen(false);
        setIsPesagemModalOpen(false);
        setIsGlicemiaModalOpen(false);
        
        // Abre o modal correspondente
        switch (modalType) {
            case 'sinaisVitais':
                setIsSinaisVitaisModalOpen(true);
                break;
            case 'alimentacao':
                setIsAlimentacaoModalOpen(true);
                break;
            case 'higiene':
                setIsHigieneModalOpen(true);
                break;
            case 'posicionamento':
                setIsPosicionamentoModalOpen(true);
                break;
            case 'atividadeFisica':
                setIsAtividadeFisicaModalOpen(true);
                break;
            case 'pesagem':
                setIsPesagemModalOpen(true);
                break;
            case 'glicemia':
                setIsGlicemiaModalOpen(true);
                break;
            case 'observacaoLivre':
                setIsObservacaoLivreModalOpen(true);
                break;
            default:
                setIsConfirmModalOpen(true);
                break;
        }
    };

    // Handler para confirmar conclusão de tarefa (modal padrão)
    const handleConfirmarTarefa = async () => {
        if (!tarefaSelecionada) return;
        
        try {
          const response = await concluirTarefa(tarefaSelecionada.id);

          // Verifica se o backend retornou um registro de diário (para medicação, por exemplo)
          if (response?.novo_registro_diario || response?.registro_diario){
            fetchRegistros();
          }
          removeTarefaDaLista(tarefaSelecionada.id);
          
          // Recarrega as tarefas concluídas para atualizar o progresso
          await buscarTarefasConcluidasDoDia();

          setIsConfirmModalOpen(false);
          setTarefaSelecionada(null);
            
        } catch (err) {
            alert("Erro ao concluir tarefa: " + err.message);
            setIsConfirmModalOpen(false);
            setTarefaSelecionada(null);
        }
    };

    // Handler para confirmar conclusão de tarefa de Sinais Vitais
    const handleConfirmarSinaisVitais = async (dadosSinaisVitais) => {
        if (!tarefaSelecionada) return;

        try {
            // Chama a função de conclusão com os dados de sinais vitais
            const response = await concluirTarefa(
                tarefaSelecionada.id,
                dadosSinaisVitais.observacoes,
                dadosSinaisVitais.sinaisVitais
            );

            // Atualiza a lista de registros se houver novo registro
            if (response?.novo_registro_diario || response?.registro_diario) {
                fetchRegistros();
            }
            
            // Remove a tarefa da lista
            removeTarefaDaLista(tarefaSelecionada.id);
            
            // Recarrega as tarefas concluídas para atualizar o progresso
            await buscarTarefasConcluidasDoDia();

            // Fecha o modal e limpa a tarefa selecionada
            setIsSinaisVitaisModalOpen(false);
            setTarefaSelecionada(null);
            
        } catch (err) {
            console.error('Erro ao concluir tarefa de sinais vitais:', err);
            alert("Erro ao registrar sinais vitais e concluir tarefa: " + err.message);
            // Não fecha o modal em caso de erro para o usuário poder tentar novamente
        }
    };

    // Handler genérico para concluir tarefa após criar registro
    const handleConcluirTarefaAposRegistro = async (modalType) => {
        if (!tarefaSelecionada) return;

        try {
            // Conclui a tarefa
            const response = await concluirTarefa(tarefaSelecionada.id);
            
            // Atualiza a lista de registros
            if (response?.novo_registro_diario || response?.registro_diario) {
                fetchRegistros();
            } else {
                fetchRegistros(); // Atualiza mesmo assim para pegar o registro criado pelo modal
            }
            
            // Remove a tarefa da lista de pendentes
            removeTarefaDaLista(tarefaSelecionada.id);
            
            // Recarrega as tarefas concluídas para atualizar o progresso
            await buscarTarefasConcluidasDoDia();

            // Fecha o modal correspondente
            switch (modalType) {
                case 'alimentacao':
                    setIsAlimentacaoModalOpen(false);
                    break;
                case 'higiene':
                    setIsHigieneModalOpen(false);
                    break;
                case 'posicionamento':
                    setIsPosicionamentoModalOpen(false);
                    break;
                case 'observacaoLivre':
                    setIsObservacaoLivreModalOpen(false);
                    break;
                case 'atividadeFisica':
                    setIsAtividadeFisicaModalOpen(false);
                    break;
                case 'pesagem':
                    setIsPesagemModalOpen(false);
                    break;
                case 'glicemia':
                    setIsGlicemiaModalOpen(false);
                    break;
            }

            // Limpa a tarefa selecionada
            setTarefaSelecionada(null);
        } catch (err) {
            console.error('Erro ao concluir tarefa:', err);
            alert("Erro ao concluir tarefa: " + err.message);
            // Não fecha o modal em caso de erro
        }
    };

    // Obtém a data/hora de início do plantão atual
    const getInicioPlantao = useCallback(() => {
        if (!pontoStatus?.data) return null;
        
        // Prioriza check_in_time, se não existir usa data_hora_inicio do agendamento
        const checkInTime = pontoStatus.data.check_in_time;
        const dataHoraInicio = pontoStatus.data.data_hora_inicio;
        
        if (checkInTime) {
            return new Date(checkInTime);
        }
        if (dataHoraInicio) {
            return new Date(dataHoraInicio);
        }
        
        return null;
    }, [pontoStatus]);

    // Filtra registros do plantão atual
    const registrosDoPlantao = useMemo(() => {
        const inicioPlantao = getInicioPlantao();
        if (!inicioPlantao) return registros; // Se não há plantão, retorna todos
        
        return registros.filter(registro => {
            if (!registro.data_hora_registro) return false;
            const dataRegistro = new Date(registro.data_hora_registro);
            return dataRegistro >= inicioPlantao;
        });
    }, [registros, getInicioPlantao]);

    // Filtra tarefas concluídas do plantão atual para incluir no histórico
    const tarefasConcluidasDoPlantao = useMemo(() => {
        const inicioPlantao = getInicioPlantao();
        if (!inicioPlantao) return []; // Se não há plantão, não mostra tarefas concluídas
        
        return tarefasConcluidas.filter(tarefa => {
            // Tarefas concluídas que foram concluídas durante o plantão
            // Verifica diferentes campos possíveis de data de conclusão
            const dataConclusao = tarefa.hora_conclusao || tarefa.data_conclusao || tarefa.hora_criacao;
            
            if (dataConclusao) {
                const dataConclusaoObj = new Date(dataConclusao);
                if (!isNaN(dataConclusaoObj.getTime())) {
                    return dataConclusaoObj >= inicioPlantao;
                }
            }
            
            // Se não tem data de conclusão, verifica se foi agendada para o plantão
            if (tarefa.hora_agendada) {
                const horaAgendada = new Date(tarefa.hora_agendada);
                if (!isNaN(horaAgendada.getTime())) {
                    return horaAgendada >= inicioPlantao;
                }
            }
            
            // Tarefas sem horário são consideradas do plantão se foram concluídas hoje
            // (assumindo que se não tem data, foi concluída recentemente)
            return true;
        });
    }, [tarefasConcluidas, getInicioPlantao]);

    // Calcular tarefas atrasadas do plantão atual
    const calcularTarefasAtrasadas = useCallback(() => {
        const agora = new Date();
        const inicioPlantao = getInicioPlantao();
        
        return tarefas.filter(t => {
            // Verifica se é tarefa atrasada
            const isAtrasada = t.status === 'Atrasada' || 
                (t.hora_agendada && (() => {
                    const horaAgendada = new Date(t.hora_agendada);
                    if (isNaN(horaAgendada.getTime())) return false;
                    const diferencaMinutos = (agora - horaAgendada) / (1000 * 60);
                    return diferencaMinutos > 15 && (t.status === 'Pendente' || !t.status);
                })());
            
            if (!isAtrasada) return false;
            
            // Se há plantão ativo, filtra apenas tarefas do plantão
            if (inicioPlantao) {
                // Tarefas agendadas para o plantão ou sem horário (considera do plantão)
                if (!t.hora_agendada) return true; // Tarefas sem horário são do plantão
                const horaAgendada = new Date(t.hora_agendada);
                return horaAgendada >= inicioPlantao;
            }
            
            return true;
        });
    }, [tarefas, getInicioPlantao]);

    // Efeitos
    useEffect(() => {
        fetchPontoStatus();
    }, [fetchPontoStatus]);

    useEffect(() => {
        if (!isPontoLoading && pacienteIdEmAtendimento) {
            loadPaciente(pacienteIdEmAtendimento);
            fetchTarefasPendentes();
            buscarTarefasConcluidasDoDia();
            fetchRegistros();
        }
    }, [pacienteIdEmAtendimento, isPontoLoading, loadPaciente, fetchTarefasPendentes, buscarTarefasConcluidasDoDia, fetchRegistros]);

    const isGlobalLoading =
        isPontoLoading || isLoadingPaciente || isLoadingTarefas || isLoadingRegistros || isLoadingConcluidas;
    // 🛑 CORREÇÃO: Não tratar errorTarefas como erro global se for 403 (sem permissão)
    // errorTarefas será null se for 403, então não bloqueia a renderização
    const globalError =
        pontoError || pacienteError || errorRegistros;

    if (globalError) return <Alert type="error" message={`Erro: ${globalError}`} />;

    if (isGlobalLoading) return <LoadingIndicator message="Carregando informações do Diário..." />;

    if (!pacienteIdEmAtendimento) {
        return (
            <div className="p-6">
                <Alert
                    type="info"
                    message="Você não está em atendimento ativo. Realize o Check-in no painel de ponto para registrar cuidados."
                />
            </div>
        );
    }

    // Layout focado em Checklist Prioritário
    return (
        <div className="p-4 lg:p-6 space-y-6">
            {/* Layout Responsivo: Desktop (lado a lado) / Mobile (empilhado) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* ÁREA PRIMÁRIA: Checklist de Tarefas */}
                <div className="order-1 space-y-6">
                    <ChecklistPrioritario
                        tarefas={tarefas}
                        onConcluir={handleConcluirTarefa}
                    />
                    
                    {/* Indicador de Progresso - Abaixo do Checklist */}
                    <IndicadorProgressoTarefas tarefas={todasTarefasDoDia} />
                </div>

                {/* ÁREA SECUNDÁRIA: Histórico do Plantão (Timeline) */}
                <div className="order-2">
                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">
                            📜 Histórico do Plantão
                        </h2>
                        <div className="max-h-[70vh] overflow-y-auto">
                            <TimelineHistorico
                                registros={registrosDoPlantao}
                                tarefasAtrasadas={calcularTarefasAtrasadas()}
                                tarefasConcluidas={tarefasConcluidasDoPlantao}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Botão Flutuante para Registro Manual */}
            <BotaoRegistroFlutuante
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={handleRegistroManualSucesso}
            />

            {/* Modais */}
            <ConfirmacaoModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                titulo="Confirmar Conclusão"
                mensagem={`Deseja marcar a tarefa "${tarefaSelecionada?.descricao}" como concluída? Esta ação debitará insumos se for uma medicação.`}
                onConfirm={handleConfirmarTarefa}
                confirmText="✅ Concluir Tarefa"
                cancelText="Cancelar"
            />

            <SinaisVitaisModal
                isOpen={isSinaisVitaisModalOpen}
                onClose={() => {
                    setIsSinaisVitaisModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                tarefa={tarefaSelecionada}
                onConfirm={handleConfirmarSinaisVitais}
            />

            {/* Modal de Alimentação */}
            <ModalAlimentacao
                isOpen={isAlimentacaoModalOpen}
                onClose={() => {
                    setIsAlimentacaoModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('alimentacao');
                }}
            />

            {/* Modal de Higiene */}
            <ModalHigiene
                isOpen={isHigieneModalOpen}
                onClose={() => {
                    setIsHigieneModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('higiene');
                }}
            />

            {/* Modal de Posicionamento */}
            <ModalPosicionamento
                isOpen={isPosicionamentoModalOpen}
                onClose={() => {
                    setIsPosicionamentoModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('posicionamento');
                }}
            />

            {/* Modal de Observação Livre */}
            <ModalObservacaoLivre
                isOpen={isObservacaoLivreModalOpen}
                onClose={() => {
                    setIsObservacaoLivreModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('observacaoLivre');
                }}
            />

            {/* Modal de Atividade Física */}
            <ModalAtividadeFisica
                isOpen={isAtividadeFisicaModalOpen}
                onClose={() => {
                    setIsAtividadeFisicaModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('atividadeFisica');
                }}
            />

            {/* Modal de Pesagem */}
            <ModalPesagem
                isOpen={isPesagemModalOpen}
                onClose={() => {
                    setIsPesagemModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('pesagem');
                }}
            />

            {/* Modal de Glicemia */}
            <ModalGlicemia
                isOpen={isGlicemiaModalOpen}
                onClose={() => {
                    setIsGlicemiaModalOpen(false);
                    setTarefaSelecionada(null);
                }}
                pacienteId={pacienteIdEmAtendimento}
                onCreateRegistro={createRegistro}
                onRegistroSucesso={async () => {
                    await handleConcluirTarefaAposRegistro('glicemia');
                }}
            />
        </div>
    );
};

export default DiarioCuidadosPage;